export type ThemeId = 'light' | 'dark' | 'midnight' | 'creamy' | 'dtg-dark' | 'dtg-light';

export interface SidebarVars {
    bgColor: string;
    bgColorAlt: string;
    border: string;
    appNameColor: string;
    menuSeparatorBorder: string;
    menuitemRootTextColor: string;
    menuitemTextColor: string;
    menuitemHoverBg: string;
    menuitemActiveBg: string;
    menuitemTextActiveColor: string;
    menuitemFocusShadow: string;
}

export interface StorefrontVars {
    // Legacy brand colors (kept for backward compat during migration)
    gold: string;
    goldDim: string;
    goldFaint: string;
    goldHover: string;
    goldContrast: string;
    crimson: string;
    crimsonDim: string;
    crimsonContrast: string;
    headerBg: string;
    headerBgScrolled: string;
    logoFilter: string;

    // Monochrome palette tokens
    bg: string;
    surface: string;
    surfaceAlt: string;
    text: string;
    textMuted: string;
    primary: string;
    primaryContrast: string;
    accent: string;
    accentHover: string;
    accentContrast: string;
    border: string;
    borderSubtle: string;
}

export interface ThemeDefinition {
    id: ThemeId;
    label: string;
    color: string;
    colorScheme: 'light' | 'dark';
    logoVariant: 'dark' | 'white';
    preset: Record<string, unknown>;
    sidebar: SidebarVars;
    storefront?: StorefrontVars;
}

// ── Light theme ──────────────────────────────────────────────────────
// Uses base AppTheme as-is (no preset overrides needed).
const lightTheme: ThemeDefinition = {
    id: 'light',
    label: 'Light',
    color: '#cecece',
    colorScheme: 'light',
    logoVariant: 'white',
    preset: {},
    sidebar: {
        bgColor: '#1f2d40',
        bgColorAlt: '#1f2d40',
        border: '1px solid #304562',
        appNameColor: 'var(--text-color)',
        menuSeparatorBorder: '1px solid #304562',
        menuitemRootTextColor: 'rgba(255,255,255,0.6)',
        menuitemTextColor: 'rgba(255,255,255,0.8)',
        menuitemHoverBg: 'rgba(255,255,255,.03)',
        menuitemActiveBg: '#283951',
        menuitemTextActiveColor: '#ffffff',
        menuitemFocusShadow: '0 0 0 1px #BBDEFB',
    },
    storefront: {
        gold: '#D4A04C',
        goldDim: 'rgba(212,160,76,0.2)',
        goldFaint: 'rgba(212,160,76,0.08)',
        goldHover: '#F0C060',
        goldContrast: '#0A0A0A',
        crimson: '#C02040',
        crimsonDim: 'rgba(192,32,64,0.15)',
        crimsonContrast: '#ffffff',
        headerBg: 'rgba(250,248,246,0.92)',
        headerBgScrolled: 'rgba(240,237,232,0.98)',
        logoFilter: 'brightness(0)',
        bg: '#FAFAFA',
        surface: '#FFFFFF',
        surfaceAlt: '#F5F5F5',
        text: '#111111',
        textMuted: '#6B6B6B',
        primary: '#111111',
        primaryContrast: '#FFFFFF',
        accent: '#a41034',
        accentHover: '#8a0d2c',
        accentContrast: '#FFFFFF',
        border: '#E5E5E5',
        borderSubtle: 'rgba(0,0,0,0.06)',
    },
};

// ── Dark theme ───────────────────────────────────────────────────────
// Neutral dark with true gray surfaces (not blueish).
const DARK_NEUTRAL_SURFACE = {
    0: '#ffffff',
    50: '#fafafa',
    100: '#d4d4d4',
    200: '#a3a3a3',
    300: '#737373',
    400: '#525252',
    500: '#404040',
    600: '#363636',
    700: '#2a2a2a',
    800: '#1f1f1f',
    900: '#171717',
    950: '#0f0f0f',
};

const darkTheme: ThemeDefinition = {
    id: 'dark',
    label: 'Dark',
    color: '#171717',
    colorScheme: 'dark',
    logoVariant: 'white',
    preset: {
        semantic: {
            colorScheme: {
                dark: {
                    surface: { ...DARK_NEUTRAL_SURFACE },
                },
            },
        },
    },
    sidebar: {
        bgColor: '#171717',
        bgColorAlt: '#1f1f1f',
        border: '1px solid #363636',
        appNameColor: '#e5e5e5',
        menuSeparatorBorder: '1px solid #363636',
        menuitemRootTextColor: 'rgba(229,229,229,0.6)',
        menuitemTextColor: 'rgba(229,229,229,0.8)',
        menuitemHoverBg: 'rgba(255,255,255,.05)',
        menuitemActiveBg: '#2a2a2a',
        menuitemTextActiveColor: '#ffffff',
        menuitemFocusShadow: '0 0 0 1px rgba(163,163,163,0.4)',
    },
    storefront: {
        gold: '#D4A04C',
        goldDim: 'rgba(212,160,76,0.2)',
        goldFaint: 'rgba(212,160,76,0.08)',
        goldHover: '#F0C060',
        goldContrast: '#0A0A0A',
        crimson: '#C02040',
        crimsonDim: 'rgba(192,32,64,0.15)',
        crimsonContrast: '#ffffff',
        headerBg: 'rgba(23,23,23,0.92)',
        headerBgScrolled: 'rgba(17,17,17,0.98)',
        logoFilter: 'brightness(0) invert(1)',
        bg: '#0A0A0A',
        surface: '#171717',
        surfaceAlt: '#1F1F1F',
        text: '#FAFAFA',
        textMuted: '#999999',
        primary: '#FAFAFA',
        primaryContrast: '#0A0A0A',
        accent: '#a41034',
        accentHover: '#c4143f',
        accentContrast: '#FFFFFF',
        border: '#2A2A2A',
        borderSubtle: 'rgba(255,255,255,0.06)',
    },
};

// ── Midnight theme ───────────────────────────────────────────────────
// Blueish dark theme - uses base AppTheme dark mode as-is.
const midnightTheme: ThemeDefinition = {
    id: 'midnight',
    label: 'Midnight',
    color: '#0c121a',
    colorScheme: 'dark',
    logoVariant: 'white',
    preset: {},
    sidebar: {
        bgColor: '#1f2d40',
        bgColorAlt: '#1f2d40',
        border: '1px solid #304562',
        appNameColor: '#ffffff',
        menuSeparatorBorder: '1px solid #304562',
        menuitemRootTextColor: 'rgba(255,255,255,0.6)',
        menuitemTextColor: 'rgba(255,255,255,0.8)',
        menuitemHoverBg: 'rgba(255,255,255,.03)',
        menuitemActiveBg: '#283951',
        menuitemTextActiveColor: '#ffffff',
        menuitemFocusShadow: '0 0 0 1px #BBDEFB',
    },
    storefront: {
        gold: '#D4A04C',
        goldDim: 'rgba(212,160,76,0.2)',
        goldFaint: 'rgba(212,160,76,0.08)',
        goldHover: '#F0C060',
        goldContrast: '#0A0A0A',
        crimson: '#C02040',
        crimsonDim: 'rgba(192,32,64,0.15)',
        crimsonContrast: '#ffffff',
        headerBg: 'rgba(12,18,26,0.92)',
        headerBgScrolled: 'rgba(10,15,22,0.98)',
        logoFilter: 'brightness(0) invert(1)',
        bg: '#0A0A0A',
        surface: '#171717',
        surfaceAlt: '#1F1F1F',
        text: '#FAFAFA',
        textMuted: '#999999',
        primary: '#FAFAFA',
        primaryContrast: '#0A0A0A',
        accent: '#a41034',
        accentHover: '#c4143f',
        accentContrast: '#FFFFFF',
        border: '#2A2A2A',
        borderSubtle: 'rgba(255,255,255,0.06)',
    },
};

// ── Creamy theme ─────────────────────────────────────────────────────
// Warm light theme with amber primary and warm-tinted surfaces.
const CREAMY_PRIMARY = {
    50: '#fdf8f0',
    100: '#f5e6cc',
    200: '#edd4a8',
    300: '#e0b97a',
    400: '#d4a04d',
    500: '#c17f24',
    600: '#a46a1e',
    700: '#875518',
    800: '#6a4112',
    900: '#4d2f0d',
    950: '#301d08',
};

const CREAMY_LIGHT_SURFACE = {
    0: '#fefcf9',
    50: '#faf6f0',
    100: '#ede4d6',
    200: '#d5c4b0',
    300: '#b8a48a',
    400: '#8a7560',
    500: '#6b5a46',
    600: '#4a3d2e',
    700: '#332a1e',
    800: '#241d14',
    900: '#17120c',
    950: '#0d0a06',
};

const creamyTheme: ThemeDefinition = {
    id: 'creamy',
    label: 'Creamy',
    color: '#c17f24',
    colorScheme: 'light',
    logoVariant: 'white',
    preset: {
        semantic: {
            primary: { ...CREAMY_PRIMARY },
            colorScheme: {
                light: {
                    surface: { ...CREAMY_LIGHT_SURFACE },
                    primary: {
                        color: '{primary.500}',
                        contrastColor: '#ffffff',
                        hoverColor: '{primary.600}',
                        activeColor: '{primary.700}',
                    },
                    highlight: {
                        background: '{primary.50}',
                        focusBackground: '{primary.100}',
                        color: '{primary.700}',
                        focusColor: '{primary.800}',
                    },
                },
            },
        },
    },
    sidebar: {
        bgColor: '#3e2723',
        bgColorAlt: '#4e342e',
        border: '1px solid #5d4037',
        appNameColor: '#efebe9',
        menuSeparatorBorder: '1px solid #5d4037',
        menuitemRootTextColor: 'rgba(239,235,233,0.6)',
        menuitemTextColor: 'rgba(239,235,233,0.8)',
        menuitemHoverBg: 'rgba(255,255,255,.05)',
        menuitemActiveBg: '#5d4037',
        menuitemTextActiveColor: '#ffd54f',
        menuitemFocusShadow: '0 0 0 1px rgba(255,213,79,0.5)',
    },
    storefront: {
        gold: '#D4A04C',
        goldDim: 'rgba(212,160,76,0.2)',
        goldFaint: 'rgba(212,160,76,0.08)',
        goldHover: '#F0C060',
        goldContrast: '#0A0A0A',
        crimson: '#C02040',
        crimsonDim: 'rgba(192,32,64,0.15)',
        crimsonContrast: '#ffffff',
        headerBg: 'rgba(250,246,240,0.92)',
        headerBgScrolled: 'rgba(240,232,218,0.98)',
        logoFilter: 'brightness(0)',
        bg: '#FAFAFA',
        surface: '#FFFFFF',
        surfaceAlt: '#F5F5F5',
        text: '#111111',
        textMuted: '#6B6B6B',
        primary: '#111111',
        primaryContrast: '#FFFFFF',
        accent: '#a41034',
        accentHover: '#8a0d2c',
        accentContrast: '#FFFFFF',
        border: '#E5E5E5',
        borderSubtle: 'rgba(0,0,0,0.06)',
    },
};

// ── DTG Dark theme ─────────────────────────────────────────
// Brand: Gold #D4A04C, Crimson #C02040 (Radon Ways palette)
// Dark admin theme with gold primary accent - used in the CMS admin panel.
const DTG_DARK_PRIMARY = {
    50:  '#fdf8eb',
    100: '#f7eacc',
    200: '#f0d896',
    300: '#E8C464',
    400: '#D4A04C',
    500: '#D4A04C',
    600: '#A07830',
    700: '#7E5E24',
    800: '#5C441A',
    900: '#3A2A10',
    950: '#1E1508',
};

const DTG_DARK_SURFACE = {
    0:   '#ffffff',
    50:  '#faf9f7',
    100: '#f0ede6',
    200: '#d6d0c4',
    300: '#b8af9e',
    400: '#8c8172',
    500: '#665e52',
    600: '#4a4438',
    700: '#332e25',
    800: '#1f1b14',
    900: '#141009',
    950: '#0b0906',
};

const dtgDarkTheme: ThemeDefinition = {
    id: 'dtg-dark',
    label: 'DTG Dark',
    color: '#D4A04C',
    colorScheme: 'dark',
    logoVariant: 'white',
    preset: {
        primitive: {
            borderRadius: {
                none: '0',
                xs:   '0.125rem',
                sm:   '0.25rem',
                md:   '0.375rem',
                lg:   '0.5rem',
                xl:   '0.75rem',
            },
        },
        semantic: {
            primary: { ...DTG_DARK_PRIMARY },
            colorScheme: {
                dark: {
                    primary: {
                        color:             '{primary.500}',
                        contrastColor:     '#0A0A0A',
                        hoverColor:        '{primary.400}',
                        activeColor:       '{primary.300}',
                    },
                    highlight: {
                        background:        'rgba(212,160,76,0.15)',
                        focusBackground:   'rgba(212,160,76,0.2)',
                        color:             '#D4A04C',
                        focusColor:        '#F0C060',
                    },
                    surface: { ...DTG_DARK_SURFACE },
                },
            },
        },
    },
    sidebar: {
        bgColor:                 '#0A0A0A',
        bgColorAlt:              '#141414',
        border:                  '1px solid rgba(212,160,76,0.18)',
        appNameColor:            '#D4A04C',
        menuSeparatorBorder:     '1px solid rgba(212,160,76,0.12)',
        menuitemRootTextColor:   'rgba(250,248,246,0.55)',
        menuitemTextColor:       'rgba(250,248,246,0.75)',
        menuitemHoverBg:         'rgba(212,160,76,0.07)',
        menuitemActiveBg:        'rgba(212,160,76,0.14)',
        menuitemTextActiveColor: '#D4A04C',
        menuitemFocusShadow:     '0 0 0 1px rgba(212,160,76,0.35)',
    },
    storefront: {
        gold: '#D4A04C',
        goldDim: 'rgba(212,160,76,0.2)',
        goldFaint: 'rgba(212,160,76,0.08)',
        goldHover: '#F0C060',
        goldContrast: '#0A0A0A',
        crimson: '#C02040',
        crimsonDim: 'rgba(192,32,64,0.15)',
        crimsonContrast: '#ffffff',
        headerBg: 'rgba(10,10,10,0.92)',
        headerBgScrolled: 'rgba(17,17,17,0.98)',
        logoFilter: 'brightness(0) invert(1)',
        bg: '#0A0A0A',
        surface: '#171717',
        surfaceAlt: '#1F1F1F',
        text: '#FAFAFA',
        textMuted: '#999999',
        primary: '#FAFAFA',
        primaryContrast: '#0A0A0A',
        accent: '#a41034',
        accentHover: '#c4143f',
        accentContrast: '#FFFFFF',
        border: '#2A2A2A',
        borderSubtle: 'rgba(255,255,255,0.06)',
    },
};

// ── DTG Light theme ────────────────────────────────────────
// Brand: Crimson #C02040 primary for CTAs, Gold #D4A04C for brand accents
// Light admin theme with crimson primary - warm-white surfaces.
const DTG_LIGHT_PRIMARY = {
    50:  '#fdf0f3',
    100: '#f9d0d8',
    200: '#F0A0B0',
    300: '#D84858',
    400: '#C02040',
    500: '#C02040',
    600: '#901020',
    700: '#700818',
    800: '#500410',
    900: '#300208',
    950: '#180104',
};

const DTG_LIGHT_SURFACE = {
    0:   '#FFFFFF',
    50:  '#FAF8F6',
    100: '#F0EDE8',
    200: '#DDD8D0',
    300: '#B8B0A4',
    400: '#8C8478',
    500: '#666058',
    600: '#4A4640',
    700: '#332F2A',
    800: '#1F1C18',
    900: '#14120E',
    950: '#0A0906',
};

const dtgLightTheme: ThemeDefinition = {
    id: 'dtg-light',
    label: 'DTG Light',
    color: '#C02040',
    colorScheme: 'light',
    logoVariant: 'dark',
    preset: {
        primitive: {
            borderRadius: {
                none: '0',
                xs:   '0.125rem',
                sm:   '0.25rem',
                md:   '0.375rem',
                lg:   '0.5rem',
                xl:   '0.75rem',
            },
        },
        semantic: {
            primary: { ...DTG_LIGHT_PRIMARY },
            colorScheme: {
                light: {
                    primary: {
                        color:             '{primary.500}',
                        contrastColor:     '#FFFFFF',
                        hoverColor:        '{primary.600}',
                        activeColor:       '{primary.700}',
                    },
                    highlight: {
                        background:        'rgba(192,32,64,0.08)',
                        focusBackground:   'rgba(192,32,64,0.12)',
                        color:             '#C02040',
                        focusColor:        '#901020',
                    },

                },
            },
        },
    },
    sidebar: {
        bgColor:                 '#FAF8F6',
        bgColorAlt:              '#F0EDE8',
        border:                  '1px solid rgba(192,32,64,0.15)',
        appNameColor:            '#C02040',
        menuSeparatorBorder:     '1px solid rgba(192,32,64,0.1)',
        menuitemRootTextColor:   'rgba(10,10,10,0.55)',
        menuitemTextColor:       'rgba(10,10,10,0.75)',
        menuitemHoverBg:         'rgba(192,32,64,0.06)',
        menuitemActiveBg:        'rgba(192,32,64,0.1)',
        menuitemTextActiveColor: '#C02040',
        menuitemFocusShadow:     '0 0 0 1px rgba(192,32,64,0.3)',
    },
    storefront: {
        gold: '#D4A04C',
        goldDim: 'rgba(212,160,76,0.2)',
        goldFaint: 'rgba(212,160,76,0.08)',
        goldHover: '#F0C060',
        goldContrast: '#0A0A0A',
        crimson: '#C02040',
        crimsonDim: 'rgba(192,32,64,0.15)',
        crimsonContrast: '#ffffff',
        headerBg: 'rgba(250,248,246,0.92)',
        headerBgScrolled: 'rgba(240,237,232,0.98)',
        logoFilter: 'brightness(0)',
        bg: '#FAFAFA',
        surface: '#FFFFFF',
        surfaceAlt: '#F5F5F5',
        text: '#111111',
        textMuted: '#6B6B6B',
        primary: '#111111',
        primaryContrast: '#FFFFFF',
        accent: '#a41034',
        accentHover: '#8a0d2c',
        accentContrast: '#FFFFFF',
        border: '#E5E5E5',
        borderSubtle: 'rgba(0,0,0,0.06)',
    },
};

export const APP_THEMES: ThemeDefinition[] = [
    lightTheme,
    darkTheme,
    midnightTheme,
    creamyTheme,
    dtgDarkTheme,
    dtgLightTheme,
];

export const DEFAULT_THEME_ID: ThemeId = 'light';

/**
 * Returns storefront vars for a theme. All themes in APP_THEMES have
 * storefront vars defined, but this provides a fallback for safety.
 */
export function getStorefrontVars(theme: ThemeDefinition): StorefrontVars {
    if (theme.storefront) return theme.storefront;

    // Fallback based on color scheme
    if (theme.colorScheme === 'dark') {
        return dtgDarkTheme.storefront!;
    }
    return dtgLightTheme.storefront!;
}
